import React from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { CalendarDays, TrendingUp, TrendingDown } from "lucide-react";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const label = (k) => `${MONTHS[Number(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}`;
const eur = (v) => `${(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
const pct = (v) => `${(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;

function CurrentMonth({ cur, prev }) {
    const diff = cur.margin_ht - (prev?.margin_ht || 0);
    const Up = diff >= 0 ? TrendingUp : TrendingDown;
    return (
        <div className="rounded-2xl border border-orange-500/30 bg-orange-500/15 p-5" data-testid="dash-month-card">
            <div className="flex items-center justify-between">
                <div className="text-xs text-slate-400 uppercase tracking-widest">Marge du mois — {label(cur.month)}</div>
                <CalendarDays size={16} className="text-orange-300" />
            </div>
            <div className="mt-3 font-display text-3xl font-bold text-white" data-testid="dash-month-margin">{eur(cur.margin_ht)}</div>
            <div className="text-xs text-slate-300 mt-1" data-testid="dash-month-rate">Taux : {pct(cur.margin_rate)} · {cur.ot_count} OT</div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                <div><div className="text-slate-500">Acheté HT</div><div className="font-mono text-white" data-testid="dash-month-purchase">{eur(cur.purchase_ht)}</div></div>
                <div><div className="text-slate-500">Vendu HT</div><div className="font-mono text-white" data-testid="dash-month-sale">{eur(cur.sale_ht)}</div></div>
            </div>
            {prev && (
                <div className={`mt-4 text-xs flex items-center gap-1.5 ${diff >= 0 ? "text-emerald-300" : "text-red-300"}`} data-testid="dash-month-diff">
                    <Up size={13} /> {diff >= 0 ? "+" : ""}{eur(diff)} vs {label(prev.month)}
                </div>
            )}
            {cur.ot_missing_purchase > 0 && <div className="text-[11px] text-amber-300 mt-2">{cur.ot_missing_purchase} OT du mois sans prix d'achat (exclus)</div>}
        </div>
    );
}

export default function MonthlyMargin({ monthly }) {
    if (!monthly?.length) return null;
    const cur = monthly[monthly.length - 1];
    const prev = monthly[monthly.length - 2];
    const data = monthly.map((m) => ({ ...m, name: label(m.month) }));
    return (
        <section className="mt-6 grid gap-4 lg:grid-cols-[320px_1fr]" data-testid="dashboard-monthly">
            <CurrentMonth cur={cur} prev={prev} />
            <div className="rounded-2xl border border-slate-800 bg-[#0f172a] p-5">
                <div className="text-xs text-slate-400 uppercase tracking-widest mb-3">Évolution sur 12 mois (date d'enlèvement)</div>
                <div className="h-64" data-testid="dash-monthly-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${(v / 1000).toLocaleString("fr-FR")}k`} />
                            <Tooltip formatter={(v, n) => [eur(v), n]} labelStyle={{ color: "#0b2545" }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="sale_ht" name="Vendu HT" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="margin_ht" name="Marge HT" fill="#f26b1d" radius={[4, 4, 0, 0]} />
                            <Line dataKey="purchase_ht" name="Acheté HT" stroke="#0b2545" strokeWidth={2} dot={false} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </section>
    );
}
